<script lang="ts" setup>
import { computed, ref, render, watch, h, onMounted } from 'vue';
import { useElementSize } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import type { HotelAmenityData } from '@apps/main/new-desktop/core/models';

import { AppSvgIcon } from '@libs/ui';

interface HotelAmenityDataWithSize extends HotelAmenityData {
  size: number;
}

interface AccommodationAmenitiesProps {
  amenities: HotelAmenityData[][];
}

const props = defineProps<AccommodationAmenitiesProps>();

const amenitiesMobileRef = ref(null);
const amenitiesWithSizes = ref<HotelAmenityDataWithSize[]>([]);
const { width } = useElementSize(amenitiesMobileRef);
const browserStore = useBrowserStore();

watch(props, () => {
  updateAmenitiesWithSizes();
});

onMounted(() => {
  updateAmenitiesWithSizes();
});

const mobileAmenities = computed<HotelAmenityDataWithSize[]>(() => {
  const result: HotelAmenityDataWithSize[] = [];
  let currentWidth = 0;

  amenitiesWithSizes.value.forEach((amenity) => {
    if (currentWidth + amenity.size < width.value || result.length === 0) {
      result.push(amenity);
      currentWidth += amenity.size;
    }
  });

  return result;
});

function updateAmenitiesWithSizes(): void {
  amenitiesWithSizes.value = props.amenities
    .flat()
    .map((amenity: HotelAmenityData) => ({
      ...amenity,
      size: getElementSize(amenity),
    }));
}

function getElementSize(amenity: HotelAmenityData): number {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.visibility = 'hidden';

  container.style.whiteSpace = 'nowrap';
  document.body.appendChild(container);

  const iconWidth = 18;
  const iconAmenitySpacing = 8;
  const amenitiyDiv = h(
    'div',
    { style: 'font: var(--font-body-s-light)' },
    amenity.value
  );
  const amenityAmenitySpacing = 16;
  const rightPadding = 4;

  render(amenitiyDiv, container);

  const width = container.offsetWidth;

  document.body.removeChild(container);
  return (
    width +
    iconWidth +
    iconAmenitySpacing +
    amenityAmenitySpacing +
    rightPadding
  );
}
</script>

<template>
  <div
    v-if="browserStore.isSmallerThan600px"
    ref="amenitiesMobileRef"
    class="accommodation-amenities-mobile"
  >
    <div
      v-for="amenity in mobileAmenities"
      :key="amenity.value"
      class="amenity"
    >
      <AppSvgIcon :name="amenity.icon" size="18" color="color-secondary-60" />
      <span class="amenity-text">{{ amenity.value }}</span>
    </div>
  </div>

  <div v-else class="accommodation-amenities-desktop">
    <div
      v-for="(amenityGroup, index) in props.amenities"
      :key="index"
      class="amenity-column"
    >
      <div v-for="amenity in amenityGroup" :key="amenity.value" class="amenity">
        <AppSvgIcon :name="amenity.icon" color="color-secondary-60" size="18" />
        <span class="amenity-text">{{ amenity.value }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.accommodation-amenities-desktop {
  margin-top: var(--spacing-16);
  display: flex;
  flex-wrap: wrap;
  column-gap: var(--spacing-24);
  row-gap: var(--spacing-8);

  .amenity-column {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);

    .amenity {
      display: flex;
      align-items: center;
      gap: var(--spacing-8);

      .amenity-text {
        white-space: nowrap;
        color: var(--color-secondary-60);
        @include font-body-m-light;
      }
    }
  }
}

.accommodation-amenities-mobile {
  margin-top: var(--spacing-12);
  display: flex;
  gap: var(--spacing-16);

  .amenity {
    display: flex;
    align-items: center;
    gap: var(--spacing-8);

    .amenity-text {
      white-space: nowrap;
      color: var(--color-secondary-60);
      @include font-body-s-light;
    }
  }
}
</style>
